import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface DeleteAccountDialogProps {
  userId: string;
}

export function DeleteAccountDialog({ userId }: DeleteAccountDialogProps) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleDelete = async () => {
    if (confirmText !== 'EXCLUIR') {
      toast.error('Digite EXCLUIR para confirmar');
      return;
    }

    setIsLoading(true);

    try {
      // Delete user data from tables (cascade will handle some, but we explicitly delete)
      const { error: diagnosticsError } = await supabase
        .from('plant_diagnostics')
        .delete()
        .eq('user_id', userId);

      if (diagnosticsError) {
        console.error('Error deleting diagnostics:', diagnosticsError);
      }

      const { error: identificationsError } = await supabase
        .from('plant_identifications')
        .delete()
        .eq('user_id', userId);

      if (identificationsError) {
        console.error('Error deleting identifications:', identificationsError);
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', userId);

      if (profileError) {
        console.error('Error deleting profile:', profileError);
      }

      // Call edge function to delete user from auth.users
      const { error: deleteUserError } = await supabase.functions.invoke('delete-user', {
        body: { userId },
      });

      if (deleteUserError) {
        throw deleteUserError;
      }

      toast.success('Conta excluída com sucesso');
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Erro ao excluir conta. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="outline" className="w-full border-destructive/30 text-destructive hover:bg-destructive/10">
          <Trash2 className="mr-2 h-4 w-4" />
          Excluir minha conta
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-destructive">Excluir conta</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            <span className="block">
              Esta ação é <strong>irreversível</strong>. Todos os seus dados serão permanentemente excluídos:
            </span>
            <ul className="list-disc list-inside text-sm space-y-1">
              <li>Todas as suas plantas identificadas</li>
              <li>Todos os diagnósticos realizados</li>
              <li>Seu perfil e informações pessoais</li>
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Label htmlFor="confirm" className="text-sm font-medium">
            Digite <span className="font-bold text-destructive">EXCLUIR</span> para confirmar
          </Label>
          <Input
            id="confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="EXCLUIR"
            className="mt-2"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setConfirmText('')}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={confirmText !== 'EXCLUIR' || isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? 'Excluindo...' : 'Excluir permanentemente'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
